import { fetchAuthSession } from 'aws-amplify/auth';
import AWS, { CognitoIdentityCredentials } from 'aws-sdk';
import { AwsClient } from 'aws4fetch';

const host = import.meta.env.VITE_PUBLIC_API_GATEWAY_URL as string;
const authHost = import.meta.env.VITE_PRIVATE_API_GATEWAY_URL as string;
const region = import.meta.env.VITE_AWS_REGION as string;

interface ServiceErrorBody {
  statusCode: number;
  msg: string;
  name?: string;
}

interface FetchRequest {
  url: string;
  method: string;
  headers?: { [key: string]: string };
  body?: BodyInit;
}

const _getAWSUnauthenticatedCredentials = async (): Promise<
  AWS.CognitoIdentityCredentials | undefined
> => {
  try {
    AWS.config.update({ region: import.meta.env.VITE_AWS_REGION as string });
    const credentials = new CognitoIdentityCredentials({
      AccountId: import.meta.env.VITE_AWS_ACCOUNT_ID as string,
      IdentityPoolId: import.meta.env
        .VITE_UNAUTHENTICATED_IDENTITY_POOL_ID as string,
    });
    await credentials.getPromise();
    return credentials;
  } catch (error) {
    console.error('Error getting temporary credentials:', error);
  }
};

const _getAccessToken = async () => {
  const { tokens } = await fetchAuthSession();
  return tokens?.accessToken?.toString() ?? '';
};

const _authenticatedFetch = async ({
  url,
  method,
  headers,
  body,
}: FetchRequest): Promise<Response> => {
  const { credentials, tokens } = await fetchAuthSession();
  if (!credentials || !tokens) {
    throw new Error('Credentials not found');
  }
  const { accessKeyId, secretAccessKey, sessionToken } = credentials;
  const aws = new AwsClient({
    accessKeyId,
    secretAccessKey,
    sessionToken,
    region,
    service: 'execute-api',
  });

  return await aws.fetch(url, {
    method,
    headers: {
      'Content-Type': 'application/json',
      'id-token': tokens?.idToken?.toString() ?? '',
      ...headers,
    },
    body,
  });
};

const _unauthenticatedFetch = async ({
  url,
  method,
  headers,
  body,
}: FetchRequest): Promise<Response> => {
  const { accessKeyId, secretAccessKey, sessionToken } =
    (await _getAWSUnauthenticatedCredentials()) as AWS.Credentials;

  const aws = new AwsClient({
    accessKeyId,
    secretAccessKey,
    sessionToken,
    region,
    service: 'execute-api',
  });

  return await aws.fetch(url, {
    method,
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
    body,
  });
};

export async function get<T>(url: string): Promise<T> {
  const response = await fetch(`${host}/${url}`, {});

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return (await response.json()) as T;
}

export async function getWithUnauthenticatedCredentials<T>(
  url: string
): Promise<T> {
  const response = await _unauthenticatedFetch({
    url: `${host}/${url}`,
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return (await response.json()) as T;
}

export async function post<T>(
  url: string,
  body: BodyInit,
  maskError = true
): Promise<T> {
  const response = await _unauthenticatedFetch({
    url: `${host}/${url}`,
    method: 'POST',
    body,
  });

  if (!response.ok) {
    if (maskError) throw new Error(`HTTP error! status: ${response.status}`);

    const errorData = (await response.json()) as ServiceErrorBody;
    throw new Error(
      errorData.msg
        ? `Error: ${errorData.msg}`
        : `HTTP error! status: ${response.status}`
    );
  }

  try {
    return (await response.json()) as T;
  } catch {
    return undefined as T;
  }
}

export async function getAuthenticated<T>(url: string): Promise<T> {
  const response = await _authenticatedFetch({
    url: `${authHost}/${url}`,
    method: 'GET',
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return (await response.json()) as T;
}

export async function postAuthenticated<T>(
  url: string,
  body: BodyInit
): Promise<T> {
  const response = await _authenticatedFetch({
    url: `${authHost}/${url}`,
    method: 'POST',
    body,
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return (await response.json()) as T;
}

export async function putAuthenticated<T>(
  url: string,
  body: BodyInit
): Promise<T> {
  const response = await _authenticatedFetch({
    url: `${authHost}/${url}`,
    method: 'PUT',
    headers: {
      'access-token': await _getAccessToken(),
    },
    body,
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return (await response.json()) as T;
}

export async function deleteAuthenticated<T>(
  url: string,
  body?: BodyInit
): Promise<T> {
  const response = await _authenticatedFetch({
    url: `${authHost}/${url}`,
    method: 'DELETE',
    body,
  });

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return (await response.json()) as T;
}

export async function getCustomURL<T>(url: string): Promise<T> {
  const response = await fetch(url, {});

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return (await response.json()) as T;
}
